import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { LogOut, Menu, X } from 'lucide-react';
import { useUserAuth } from '../context/UserAuthContext';
import { Logo, LogoIcon } from './Logo';
import config from '../../config/env';

const UserNavbar = () => {
  const { isAuthenticated, logout, isLoading } = useUserAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [navigate]);

  const handleLogout = async () => {
    setMobileMenuOpen(false);
    await logout();
    navigate('/');
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <>
      <nav className={`fixed top-[40px] sm:top-[44px] left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileMenuOpen
          ? 'bg-[#222222]/5 backdrop-blur-xl' 
          : 'bg-transparent'
      }`}>
        <div className="w-full px-4 sm:px-6 md:px-12 lg:px-20 py-4 md:py-6 flex items-center justify-between">
          {/* Logo */}
          <Link to={isAuthenticated ? "/dashboard" : "/"} className="flex items-center gap-2 text-white">
            <LogoIcon className="h-8 w-8" />
            {/* <span className="text-lg font-medium">NeoPass</span> */}
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {isLoading ? (
              <div className="w-20 h-8 bg-white/10 animate-pulse"></div>
            ) : (
              <>
                {isAuthenticated ? (
                  <>
                    <Link to="/dashboard">
                      <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10">Dashboard</Button>
                    </Link>
                    <Link to="/installation">
                      <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10">Installation</Button>
                    </Link>
                    <Link to="/guide">
                      <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10">Guide</Button>
                    </Link>
                    <Link to="/pricing">
                      <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10">Pricing</Button>
                    </Link>
                    <Link to="/must-read">
                      <Button variant="ghost" size="sm" className="text-accent hover:text-accent-light hover:bg-white/10">Must Read</Button>
                    </Link>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleLogout}
                      className="gap-2 text-white/70 hover:text-white hover:bg-white/10 ml-2"
                    >
                      <LogOut className="h-4 w-4" />
                      Logout
                    </Button>
                  </>
                ) : (
                  <>
                    <Link to="/">
                      <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10">Home</Button>
                    </Link>
                    <Link to="/installation">
                      <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10">Installation</Button>
                    </Link>
                    <Link to="/guide">
                      <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10">Guide</Button>
                    </Link>
                    <Link to="/pricing">
                      <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10">Pricing</Button>
                    </Link>
                    <Link to="/must-read">
                      <Button variant="ghost" size="sm" className="text-accent hover:text-accent-light hover:bg-white/10">Must Read</Button>
                    </Link>
                    <Link to="/login">
                      <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10">Login</Button>
                    </Link>
                    <Link to="/signup">
                      <Button size="sm" className="ml-2 bg-white text-black hover:bg-white/90 font-medium">Sign Up</Button>
                    </Link>
                  </>
                )}
                {/* Download Button - visible to all */}
                <a
                  href={config.extensionDownloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-2"
                >
                  <Button size="sm" className="text-black hover:bg-white/90 font-medium w-full">Download</Button>
                </a>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-white/70 hover:text-white transition-colors"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden transition-all duration-300 overflow-hidden ${
          mobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="px-4 pb-6 space-y-1 border-t border-white/10">
            {isLoading ? (
              <div className="w-full h-10 bg-white/10 animate-pulse mt-4"></div>
            ) : (
              <>
                {isAuthenticated ? (
                  <>
                    <Link to="/dashboard" onClick={closeMobileMenu}>
                      <div className="py-3 text-white/70 hover:text-white transition-colors border-b border-white/5">Dashboard</div>
                    </Link>
                    <Link to="/installation" onClick={closeMobileMenu}>
                      <div className="py-3 text-white/70 hover:text-white transition-colors border-b border-white/5">Installation</div>
                    </Link>
                    <Link to="/guide" onClick={closeMobileMenu}>
                      <div className="py-3 text-white/70 hover:text-white transition-colors border-b border-white/5">Guide</div>
                    </Link>
                    <Link to="/pricing" onClick={closeMobileMenu}>
                      <div className="py-3 text-white/70 hover:text-white transition-colors border-b border-white/5">Pricing</div>
                    </Link>
                    <Link to="/must-read" onClick={closeMobileMenu}>
                      <div className="py-3 text-accent hover:text-accent-light transition-colors border-b border-white/5">Must Read</div>
                    </Link>
                    <button 
                      onClick={handleLogout}
                      className="w-full py-3 flex items-center gap-2 text-white/70 hover:text-white transition-colors"
                    >
                      <LogOut className="h-4 w-4" />
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/" onClick={closeMobileMenu}>
                      <div className="py-3 text-white/70 hover:text-white transition-colors border-b border-white/5">Home</div>
                    </Link>
                    <Link to="/installation" onClick={closeMobileMenu}>
                      <div className="py-3 text-white/70 hover:text-white transition-colors border-b border-white/5">Installation</div>
                    </Link>
                    <Link to="/guide" onClick={closeMobileMenu}>
                      <div className="py-3 text-white/70 hover:text-white transition-colors border-b border-white/5">Guide</div>
                    </Link>
                    <Link to="/pricing" onClick={closeMobileMenu}>
                      <div className="py-3 text-white/70 hover:text-white transition-colors border-b border-white/5">Pricing</div>
                    </Link>
                    <Link to="/must-read" onClick={closeMobileMenu}>
                      <div className="py-3 text-accent hover:text-accent-light transition-colors border-b border-white/5">Must Read</div>
                    </Link>
                    <Link to="/login" onClick={closeMobileMenu}>
                      <div className="py-3 text-white/70 hover:text-white transition-colors border-b border-white/5">Login</div>
                    </Link>
                    <Link to="/signup" onClick={closeMobileMenu} className="block pt-4">
                      <Button className="w-full bg-white text-black hover:bg-white/90 font-medium">Sign Up</Button>
                    </Link>
                  </>
                )}
                {/* Download Button - visible to all */}
                <a
                  href={config.extensionDownloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block pt-4"
                >
                  <Button className="text-black hover:bg-white/10 font-medium w-full">Download</Button>
                </a>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Overlay for mobile menu */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </>
  );
};

export default UserNavbar;
